/**
 * Profile comparison — fetch two profiles side-by-side for the compare screen.
 */

import { supabase } from './supabase';
import { getUserDuelStats, type DuelStats } from './duelStats';

const V1_LIFT_KEYS = ['deadlift', 'bench', 'squat', 'weighted-pull-up', 'dead-hang'] as const;
type V1LiftKey = typeof V1_LIFT_KEYS[number];

const LIFT_DISPLAY_NAMES: Record<string, string> = {
  deadlift: 'Deadlift',
  bench: 'Bench Press',
  squat: 'Back Squat',
  'weighted-pull-up': 'Weighted Pull Up',
  'dead-hang': 'Dead Hang',
};

function kgToLbsRounded(kg: number): number {
  const lbs = kg * 2.20462;
  return Math.round(lbs / 5) * 5;
}

export interface LiftEntry {
  lift: string;      // display name
  liftKey: string;   // raw key
  weight: number;    // lbs, rounded to nearest 5
  unit: string;      // 'lbs'
  rawKg: number;     // for sorting
}

export interface CompareProfile {
  id: string;
  handle: string | null;
  display_name: string | null;
  avatar_url: string | null;
  duel_standard_strength_kg: number | null;
  current_rank: number | null;
  division: string | null;
  streak: number;
  totalWorkouts: number;
  duelStats: DuelStats | null;
  topLifts: LiftEntry[];
  allExercises: LiftEntry[];
}

type DuelStandardComponents = Partial<Record<string, { raw_value: number; decayed_value: number; performed_at: string }>>;

function buildLifts(components: DuelStandardComponents | null): { topLifts: LiftEntry[]; allExercises: LiftEntry[] } {
  const all: LiftEntry[] = V1_LIFT_KEYS.map(key => {
    const comp = components?.[key];
    const rawKg = comp?.raw_value ?? 0;
    return {
      lift: LIFT_DISPLAY_NAMES[key] ?? key,
      liftKey: key,
      weight: rawKg > 0 ? kgToLbsRounded(rawKg) : 0,
      unit: 'lbs',
      rawKg,
    };
  });

  // topLifts: sort descending by rawKg (all 5 present, even if 0)
  const topLifts = [...all].sort((a, b) => b.rawKg - a.rawKg);

  return { topLifts, allExercises: all };
}

async function fetchCompareProfile(userId: string): Promise<CompareProfile | null> {
  const { data: profile } = await supabase
    .from('profiles')
    .select('id, handle, display_name, avatar_url, duel_standard_strength_kg, current_rank, duel_standard_components, units')
    .eq('id', userId)
    .maybeSingle();

  if (!profile) return null;

  const [duelStats, workoutRes] = await Promise.all([
    getUserDuelStats(userId),
    supabase
      .from('workouts')
      .select('performed_at', { count: 'exact' })
      .eq('user_id', userId)
      .order('performed_at', { ascending: false })
      .limit(200),
  ]);

  // Calculate streak
  let streak = 0;
  const dates = (workoutRes.data ?? []).map(w => w.performed_at);
  if (dates.length > 0) {
    const uniqueDays = [...new Set(dates.map(d => new Date(d).toISOString().slice(0, 10)))].sort().reverse();
    const today = new Date().toISOString().slice(0, 10);
    if (uniqueDays[0] === today || dayDiff(uniqueDays[0], today) <= 1) {
      streak = 1;
      for (let i = 1; i < uniqueDays.length; i++) {
        if (dayDiff(uniqueDays[i], uniqueDays[i - 1]) <= 1) streak++;
        else break;
      }
    }
  }

  // Build lifts from duel_standard_components
  const { topLifts, allExercises } = buildLifts(profile.duel_standard_components as DuelStandardComponents | null);

  // Division from rank
  const rank = profile.current_rank as number | null;
  let division: string | null = null;
  if (typeof rank === 'number') {
    if (rank <= 10) division = 'Diamond';
    else if (rank <= 50) division = 'Platinum';
    else if (rank <= 200) division = 'Gold';
    else if (rank <= 500) division = 'Silver';
    else division = 'Bronze';
  }

  return {
    id: profile.id,
    handle: profile.handle,
    display_name: profile.display_name,
    avatar_url: profile.avatar_url,
    duel_standard_strength_kg: profile.duel_standard_strength_kg,
    current_rank: rank,
    division,
    streak,
    totalWorkouts: workoutRes.count ?? 0,
    duelStats,
    topLifts,
    allExercises,
  };
}

function dayDiff(a: string, b: string): number {
  return Math.abs((new Date(b).getTime() - new Date(a).getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Fetch both profiles for comparison.
 */
export async function fetchCompareProfiles(
  userId1: string,
  userId2: string
): Promise<{ left: CompareProfile | null; right: CompareProfile | null }> {
  const [left, right] = await Promise.all([
    fetchCompareProfile(userId1),
    fetchCompareProfile(userId2),
  ]);
  return { left, right };
}
